<template>
    <div id="residence-agreement-form">
        <v-card >
            <v-card elevation="0" v-if="showAgreement">
                <v-card-title>
                    King’s University College Residence Agreement
                </v-card-title>
                <v-card-text>
                    This Residence Agreement (the “Agreement”) is entered into by the undersigned student (“Student”) and King’s University College at Western University (“the College” or “King’s”).
                </v-card-text>
                <v-card-text>
                    By signing below, I, <span class="applicant__commonname">{{ applicant.firstName }} {{ applicant.lastName }}</span>, agree to the following terms and conditions:
                </v-card-text>
                <v-card-text class="font-weight-bold">1. Eligibility</v-card-text>
                <v-card-text>
                    The Student affirms that they are registered as a full-time student (or equivalent) at King’s University College, the University of Western Ontario or Huron University College. 
                    Should the Student cease to meet this requirement, the College reserves the right to terminate the Student’s residency. 
                    In such cases, the Student agrees to vacate the residence upon notice by the Residence and Conference Services Office (“RCSO”).
                </v-card-text>
                <v-card-text class="font-weight-bold">2. Fees</v-card-text>
                <v-card-text>
                    The Student agrees to pay all applicable residence fees, meal plan fees (where required), and incidental fees in accordance with the College’s published Fee Schedule.
                </v-card-text>
                <v-card-text class="font-weight-bold">3. Term of Residence</v-card-text>
                <v-card-text>
                    The term of this Agreement extends from Labour Day until the earlier of: <br />
                    (a) twenty-four (24) hours after the Student’s final exam, or <br />
                    (b) the final day of the examination period as indicated in the College’s academic calendar.
                </v-card-text>
                <v-card-text>
                    Residence is closed during the December holiday period unless the Student receives written permission to remain from the RCSO. 
                    Separate arrangements are required for accommodation between May and August through King’s Conference Services.
                </v-card-text>
                <v-card-text class="font-weight-bold">4. Early Termination and Refunds</v-card-text>
                <v-card-text>
                    If the Student leaves residence prior to the end of the term, they remain responsible for all fees unless formally released from the Agreement by the Dean of Students. 
                    Deposits and incidental fees are non-refundable. 
                    Refunds for room and meal plan fees are calculated based on the College’s refund schedule and policy (12% declining balance method), available on the College website. No exceptions.
                </v-card-text>
                <v-card-text class="font-weight-bold">5. Meal Plan Requirement</v-card-text>
                <v-card-text>
                    Meal plans are mandatory for residents of Alumni Court, Townhouses, Wemple. Meal plans are optional for residents of International House Epworth Place Houses and King’s Commons Apartments.
                </v-card-text>
                <v-card-text class="font-weight-bold">6. Room Use and Conditions</v-card-text>
                <v-card-text>
                    The Student acknowledges that residence space is licensed, not owned, and that the College retains possession and control at all times. 
                    The Student agrees to comply with the following: <br />
                    (a) The King’s Rules of Residence (including Rules of Residence – Houses and Apartments), the Residence Network Use Agreement, and the Alcohol Policy; <br />
                    (b) All directives, notices, and policies issued by the RCLO and Residence Staff; <br />
                    (c) The rulings of any residence disciplinary authority; <br />
                    (d) All relevant College and University policies, and directions issued by King’s Security or the Western Special Constable Service.
                </v-card-text>
                <v-card-text>
                    The RCSO reserves right to:
                    <ul>
                        <li>Reassign rooms at its sole discretion;</li>
                        <li>Enter the room at any time for maintenance, cleaning, or policy compliance;</li>
                        <li>Levy charges for any damages caused by the Student or their guests;</li>
                        <li>Impose disciplinary fines or fees where applicable.</li>
                    </ul>
                </v-card-text>
                <v-card-text class="font-weight-bold">7. Limitation of Liability</v-card-text>
                <v-card-text>
                    The Student waives all claims against the College and its representatives for any loss, damage, or injury resulting from: theft, fire, accidents, natural disasters, or enforcement/non-enforcement of College policies.
                </v-card-text>
                <v-card-text class="font-weight-bold">8. Indemnification</v-card-text>
                <v-card-text>
                    The Student agrees to indemnify and hold harmless the College, its successors, and assigns from any claims, losses, or legal proceedings arising from breaches of the rules noted in Section 6. This obligation binds the Student’s heirs and personal representatives.
                </v-card-text>
                <v-card-text class="font-weight-bold">9. Athletics and Recreation</v-card-text>
                <v-card-text>
                    The Student understands the inherent risks associated with athletic and recreational activities and confirms their physical fitness to participate. 
                    The Student waives liability against the College for any related injury or loss and agrees to indemnify the College for any resulting claims.
                </v-card-text>
                <v-card-text class="font-weight-bold">10. Academic Standing Verification</v-card-text>
                <v-card-text>
                    The Student authorizes the RCLO and/or the Dean of Students to access their academic records to verify eligibility for residence.
                </v-card-text>
                <v-card-text class="font-weight-bold">11. Breach and Termination</v-card-text>
                <v-card-text>
                    A breach of any provision of this Agreement may result in termination of residency. Upon notice, the Student agrees to vacate the premises and remove all belongings by the specified time.
                </v-card-text>
                <v-card-text class="font-weight-bold">12. Students Under Age of Majority</v-card-text>
                <v-card-text>
                    If the Student is under the age of majority, the College may contact the Student’s parent(s) or legal guardian regarding any matter related to this Agreement.
                </v-card-text>
                <v-card-text class="font-weight-bold">13. Acknowledgment and Agreement</v-card-text>
                <v-card-text>
                    By signing below, the Student confirms that they have read, understood, and agreed to all terms and conditions in this Agreement. All students, regardless of age, are encouraged to discuss this Agreement with a parent or guardian prior to signing.
                </v-card-text>
                <v-card-title class="pb-0">
                    Applicant
                </v-card-title>
                <v-form ref="residenceAgreementForm" v-model="valid" lazy-validation>
                    <v-card-text class="pt-0 pb-0">
                        <v-container fluid>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.lastName" disabled label="Surname"
                                        prepend-icon="mdi-pencil" :rules="applicantSurnameRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.firstName" disabled label="Given Names"
                                        prepend-icon="mdi-pencil" :rules="applicantGivenNamesRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.applicantSignature"
                                        label="Signature" prepend-icon="mdi-draw" :rules="applicantSignatureRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.applicantSignatureDate" disabled
                                        label="Date (YYYY-MM-DD)" prepend-icon="mdi-calendar-check"
                                        :rules="applicantSignatureDateRules" v-bind="attrs" v-on="on">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-title v-if="!getAgeVerification">
                        Parent(s) or Legal Guardian(s)
                    </v-card-title>
                    <v-card-subtitle v-if="!getAgeVerification">
                        Applicants under 18 years of age must have Parent(s) or Legal Guardian(s) complete this section of
                        the form.
                    </v-card-subtitle>
                    <v-card-text v-if="!getAgeVerification" class="pt-0 pb-0">
                        In consideration of King’s University College agreeing to accept the above-named student as a
                        resident,
                        the undersigned agrees as follows:
                        <ol>
                            <li>I am the parent/legal guardian of the above-named student; and</li>
                            <li>I will be responsible for and will assume all of the obligations and liabilities of the
                                above-named student in relation to the foregoing Agreement.</li>
                        </ol>
                        <v-container fluid>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentSurname" label="Surname"
                                        prepend-icon="mdi-pencil" :rules="parentSurnameRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentGivenNames"
                                        label="Given Names" prepend-icon="mdi-pencil" :rules="parentGivenNamesRules"
                                        required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentPhone"
                                        label="Telephone Number" prepend-icon="mdi-phone" :rules="parentPhoneRules"
                                        required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentMail" label="Email Address"
                                        prepend-icon="mdi-email" :rules="parentMailRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentSignature" label="Signature"
                                        prepend-icon="mdi-draw" :rules="parentSignatureRules" required>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="applicant.residenceAgreement.parentSignatureDate" disabled
                                        label="Date" prepend-icon="mdi-calendar-check" readonly
                                        :rules="parentSignatureDateRules">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-form>
            </v-card>
            <v-card elevation="0" v-if="!showAgreement">
                <v-card-text>
                    <v-row>
                        <v-col cols="12"> </v-col>
                        <v-col cols="12">
                            Upon review and approval of the application, and receipt of the required deposit, students will be issued a lease for signature.
                        </v-col>
                        <v-col cols="12"> </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <v-card-actions>
                <v-spacer>
                </v-spacer>
                <v-btn v-if="isResidenceAssistant == '1'" class="white--text" :color="infoColor" :disabled="!valid"
                    @click="openSubmitDialog()">
                    Submit
                </v-btn>
                <v-btn v-else class="white--text" :color="successColor" :disabled="!valid" @click="nextStep(8)">
                    Next
                </v-btn>
                <v-btn class="white--text" :color="kingsGrey" @click="prevStep(6)">
                    Previous
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog v-if="isResidenceAssistant == '1'" v-model="dialog" max-width="540">
            <v-card>
                <v-card-title>Confirmation</v-card-title>
                <v-card-text>
                    Please ensure that the information provided is accurate and reliable. If you wish to submit the
                    Online Residence Application, please click confirm.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :color="successColor" :loading="submitBool" text @click="submitForm()">
                        Confirm
                    </v-btn>
                    <v-btn :color="warningColor" text @click="closeSubmitDialog()">
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'residence-agreement-form-v1',
    props: {
        applicant: {
            required: true
        },
        isResidenceAssistant: {
            required: false
        },
        submitBool: {
            required: false
        }
    },
    data() {
        return {
            ageVerification: false,
            applicantSignatureDateRules: [
                v => !!v || 'Date is required',
            ],
            applicantGivenNamesRules: [
                v => !!v || 'Given names is required',
                v => (v && v.length >= 2) || 'Given names must be more than 2 characters'
            ],
            applicantSignatureRules: [
                v => !!v || 'Signature is required',
                v => (v && v.length >= 2) || 'Signature must be more than 2 characters'
            ],
            applicantSurnameRules: [
                v => !!v || 'Surname is required',
                v => (v && v.length >= 2) || 'Surname must be more than 2 characters'
            ],
            currentDate: new Date(),
            dialog: false,
            parentSignatureDateRules: [
                v => !!v || 'Date is required',
            ],
            parentGivenNamesRules: [
                v => !!v || 'Given names is required',
                v => (v && v.length >= 2) || 'Given names must be more than 2 characters'
            ],
            parentMailRules: [
                v => !!v || 'Email is required',
            ],
            parentPhoneRules: [
                v => !!v || 'Telephone is required',
            ],
            parentSignatureRules: [
                v => !!v || 'Signature is required',
                v => (v && v.length >= 2) || 'Signature must be more than 2 characters'
            ],
            parentSurnameRules: [
                v => !!v || 'Surname is required',
                v => (v && v.length >= 2) || 'Surname must be more than 2 characters'
            ],
            valid: true,
            version: '1'
        }
    },
    methods: {
        closeSubmitDialog() {
            this.dialog = false;
        },
        openSubmitDialog() {
            if (this.$refs.residenceAgreementForm.validate()) {
                this.dialog = true;
            }
        },
        nextStep(val) {
            if(this.showAgreement == true){
                if (this.$refs.residenceAgreementForm.validate()) {
                    this.$emit('setStep', val);
                    this.$nextTick(() => {
                        this.$emit('scrollToTop');
                    })
                }
            }else {
                this.$emit('setStep', val);
                this.$nextTick(() => {
                    this.$emit('scrollToTop');
                })
            }
            
        },
        prevStep(step) {
            this.$emit('setStep', step);
            this.$nextTick(() => {
                this.$emit('scrollToTop');
            })
        },
        submitForm() {
            this.$emit('submitForm');
        }
    },
    computed: {
        getAgeVerification() {
            let dob = '';
            let myDate = new Date();
            let currentDate;
            currentDate = (myDate.getFullYear() - 18) + ('0' + (myDate.getMonth() + 1)).slice(-2) + ('0' + myDate.getDate()).slice(-2);

            if (this.applicant.dateOfBirth) {
                dob = this.applicant.dateOfBirth.replace(/\D/g, '');

                if (parseInt(dob) <= parseInt(currentDate)) {
                    return true;
                }
            }

            return false;
        },
        infoColor() {
            return '#5578eb';
        },
        kingsGrey() {
            return '#6d6e71';
        },
        successColor() {
            return '#1bc5bd';
        },
        warningColor() {
            return '#ffb822'
        },
        showAgreement(){
            let buildingOption1 = false
            let buildingOption2 = false
            let upperyears = ["2", "3", "4"]
            let isUpperYear = false

            buildingOption1 = this.applicant.buildingOption01 != "" && (/Epworth Place/i.test(this.applicant.buildingOption01) || /King's Commons/i.test(this.applicant.buildingOption01)) ? true : false
            buildingOption2 = this.applicant.buildingOption02 != "" && (/Epworth Place/i.test(this.applicant.buildingOption02) || /King's Commons/i.test(this.applicant.buildingOption02)) ? true : false
            isUpperYear = this.applicant.yearOfStudy != "" && upperyears.includes(this.applicant.yearOfStudy)

            if ((buildingOption1 || buildingOption2) && isUpperYear){
                return false;
            }

            return true;
        }
    }
}
</script>

<style scoped>
.applicant__commonname {
    font-size: 20px;
    font-weight: bold;
    text-decoration: underline;
}

.col-12 {
    padding-bottom: 0;
    padding-top: 0;
}

div {
    color: black;
    line-height: 1.8;
}
</style>